import type React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Badge } from './ui/badge';
import LineChart, { type LinePoint, type AccentColor } from './LineChart';

interface EvolutionCardProps {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  points: LinePoint[];
  accent: AccentColor;
  currentLabel?: string;
  /** Trend is directional only; the badge is neutral so the page doesn't
   *  silently label every drop as "destructive" (a shorter game session isn't
   *  inherently bad, for example). */
  trend?: { label: string } | null;
  yFormatter?: (v: number) => string;
  xFormatter?: (v: number) => string;
  emptyText?: string;
  delay?: number;
  baseline?: 'zero' | 'auto';
}

// Monochromatic slate icon plates — mirrors the shadcn aesthetic used in the
// dashboard-react repo (chart-1 / chart-3 hues). Differentiation between the
// four cards comes from the title + icon, not from color.
const ACCENT_BG: Record<AccentColor, string> = {
  sky: 'bg-slate-900',
  amber: 'bg-slate-600',
  emerald: 'bg-slate-900',
  violet: 'bg-slate-600',
};

const EvolutionCard: React.FC<EvolutionCardProps> = ({
  title,
  subtitle,
  icon,
  points,
  accent,
  currentLabel,
  trend,
  yFormatter,
  xFormatter,
  emptyText,
  delay = 0,
  baseline = 'zero',
}) => {
  const hasData = points.length > 0;

  return (
    <Card style={{ animation: `fade-in 0.5s ease-out ${delay}ms both` }}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
        <div className="flex items-center gap-3">
          <div className={`rounded-lg p-2 ${ACCENT_BG[accent]}`}>{icon}</div>
          <div className="space-y-0.5">
            <CardTitle>{title}</CardTitle>
            {subtitle && <CardDescription>{subtitle}</CardDescription>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {currentLabel && (
            <span className="text-lg font-bold text-slate-800">
              {currentLabel}
            </span>
          )}
          {trend && <Badge variant="secondary">{trend.label}</Badge>}
        </div>
      </CardHeader>

      <CardContent className="pt-2">
        {hasData ? (
          <LineChart
            points={points}
            accent={accent}
            yFormatter={yFormatter}
            xFormatter={xFormatter}
            baseline={baseline}
          />
        ) : (
          <div className="flex h-[180px] items-center justify-center text-xs text-slate-400">
            {emptyText ?? '—'}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EvolutionCard;
